import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { PageBar, Content } from '../components/Layout'
import { Badge, Empty, ErrorBox, Loading, Panel } from '../components/ui'
import { api } from '../lib/api'
import { gbp, shortDate } from '../lib/format'
import type { Booking, Client, Contact, Invoice } from '../types'

export function Clients() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') === 'contacts' ? 'contacts' : 'clients'

  const { data, isLoading, error } = useQuery({
    queryKey: ['clients'], queryFn: () => api.get<Client[]>('/clients'),
  })
  const contacts = useQuery({
    queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts'),
  })
  const bookings = useQuery({ queryKey: ['all-bookings'], queryFn: () => api.get<Booking[]>('/bookings?days=3650') })
  const invoices = useQuery({ queryKey: ['invoices'], queryFn: () => api.get<Invoice[]>('/invoices') })

  // 341 rows and no way to search them is not a list, it is a wall. Filtering client
  // side because the whole set is already here and a round trip per keystroke would be
  // slower than the filter.
  const [find, setFind] = useState('')
  const shown = useMemo(() => {
    const all = contacts.data ?? []
    const needle = find.trim().toLowerCase()
    if (!needle) return all
    return all.filter((c) =>
      `${c.name} ${c.email} ${c.phone} ${c.segment}`.toLowerCase().includes(needle),
    )
  }, [contacts.data, find])

  return (
    <>
      <PageBar title="Clients" />
      <Content>
        <div className="tabs">
          <button
            type="button"
            className={`tab ${tab === 'clients' ? 'on' : ''}`}
            onClick={() => setParams({})}
          >
            Clients<span className="n">{data?.length ?? 0}</span>
          </button>
          <button
            type="button"
            className={`tab ${tab === 'contacts' ? 'on' : ''}`}
            onClick={() => setParams({ tab: 'contacts' })}
          >
            Contacts<span className="n">{contacts.data?.length ?? 0}</span>
          </button>
        </div>

        {tab === 'clients' && (
          <>
            <Panel>
              {isLoading && <Loading />}
              {error && <ErrorBox error={error} />}
              {data && !data.length && (
                <Empty>
                  No clients yet — a client is created the first time you reply to an
                  enquiry or book someone in.
                </Empty>
              )}
              {data && data.length > 0 && (
                <table className="cards">
                  <thead>
                    <tr>
                      <th>Name</th><th>Email</th>
                      <th style={{ width: 130 }}>Segment</th>
                      <th style={{ width: 90 }}>Bookings</th>
                      <th style={{ width: 110 }}>Billed</th>
                      <th style={{ width: 110 }}>Since</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((c) => (
                      <tr key={c.id}>
                        <td><Link to={`/clients/${c.id}`}><b>{c.name || '—'}</b></Link></td>
                        <td className="email">{c.email}</td>
                        <td>
                          {c.segment !== 'unknown'
                            ? <Badge kind="seg" value={c.segment} />
                            : <span className="muted">—</span>}
                        </td>
                        <td data-label="Bookings">{bookings.data?.filter((b) => b.client_id === c.id).length ?? 0}</td>
                        <td data-label="Billed">
                          {gbp(
                            (invoices.data ?? [])
                              .filter((i) => i.client_id === c.id && i.status === 'paid')
                              .reduce((sum, i) => sum + i.gross_pence, 0),
                          )}
                        </td>
                        <td className="when" data-label="Since">{shortDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            {contacts.data && contacts.data.length > 0 && (
              <div className="note">
                <b>{contacts.data.length} people</b> have written in over the years. They are
                under <b>Contacts</b>. They are not listed as clients because nothing in the
                old form export records whether anyone ever replied to them — and a client
                you never actually dealt with is a worse number than no number.
              </div>
            )}
          </>
        )}

        {tab === 'contacts' && (
          <>
            <div className="findrow">
              <input
                type="search"
                className="find"
                placeholder="Find a name, email, phone or segment"
                value={find}
                onChange={(e) => setFind(e.target.value)}
              />
              <span className="findcount">
                {find.trim()
                  ? `${shown.length} of ${contacts.data?.length ?? 0}`
                  : `${contacts.data?.length ?? 0} people`}
              </span>
            </div>

            <Panel>
              {contacts.isLoading && <Loading />}
              {contacts.error && <ErrorBox error={contacts.error} />}
              {contacts.data && !contacts.data.length && <Empty>Nobody has written in yet.</Empty>}
              {contacts.data && contacts.data.length > 0 && !shown.length && (
                <Empty>Nobody matches &ldquo;{find.trim()}&rdquo;.</Empty>
              )}
              {shown.length > 0 && (
                <table className="cards">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th style={{ width: 210 }}>Email</th>
                      <th style={{ width: 130 }}>Phone</th>
                      <th style={{ width: 120 }}>Asked about</th>
                      <th style={{ width: 110 }} className="hide-sm">Segment</th>
                      <th style={{ width: 70 }} className="hide-sm">Times</th>
                      <th style={{ width: 100 }}>Last wrote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((c) => (
                      <tr key={c.email}>
                        <td>
                          <b>{c.name || '—'}</b>
                          {c.is_client && <span className="badge b-client" style={{ marginLeft: 8 }}>client</span>}
                        </td>
                        <td className="email">{c.email}</td>
                        <td data-label="Phone">{c.phone || <span className="muted">—</span>}</td>
                        <td><Badge kind="b" value={c.category} /></td>
                        <td className="hide-sm">
                          {c.segment !== 'unknown'
                            ? <Badge kind="seg" value={c.segment} />
                            : <span className="muted">—</span>}
                        </td>
                        <td className="hide-sm">{c.enquiries}</td>
                        <td className="when" data-label="Last wrote">{shortDate(c.last_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Panel>

            <div className="note">
              <b>Everyone who has ever written in</b>, one row per email address — built from
              the 453 enquiries, not a separate list anyone has to maintain. Spam and test
              submissions are left out. Somebody becomes a <b>client</b> when you reply to
              them or book them in; until then they are a contact, because the old export
              records nothing about whether they were ever answered.
            </div>
          </>
        )}
      </Content>
    </>
  )
}
